import { useEffect, useState } from "react";

import dayjs from "dayjs";
import sortBy from "lodash/sortBy";
import { useSelector } from "react-redux";

import { translate } from "app/filters/i18nFilter";
import { simplePercentageFilter } from "app/filters/ngFilters";
import { selectSelectedBenchmark } from "app/pages/benchmark-tracker/store/selectors/entitySelectors";
import { selectAssetsImages } from "app/pages/benchmark-tracker/store/selectors/reportSelectors";

import { MetricFormat } from "app/common/benchmark/types";
import { DEFAULT_DECIMAL, FORMATTERS, PERCENTAGE_POINT } from "app/common/benchmark/formatters";
import type { AudienceApiSchema } from "../types/api.types";

import { MAX_COMPETITORS_COUNT, MAX_COMPETITORS_COUNT_TO_SHOW } from "./constants";
import type { TableDataSchema } from "./types";

type WidgetDataSchema = {
    tableData: Array<TableDataSchema>;
    chartData: {
        reach: number;
        primaryDomain: string;
    };
    header: {
        value: string;
        change: string;
        isIncreasing: boolean;
        isNotChanged: boolean;
    };
};

const useAudienceData = (data: AudienceApiSchema | undefined, format: MetricFormat, isLoading: boolean) => {
    const selectedBenchmark = useSelector(selectSelectedBenchmark);
    const domainImages = useSelector(selectAssetsImages);

    const [widgetsData, setWidgetsData] = useState<WidgetDataSchema>();

    useEffect(() => {
        if (!data?.competitorsData || !data?.primaryData || isLoading) {
            return;
        }

        const { competitorsData, primaryData } = data;

        const getTrend = (trends: Array<{ key: string; value: number | string }>) =>
            trends.map((item) => ({
                key: dayjs(item.key).valueOf(),
                value: Number(item.value),
            }));

        const primaryDomain = selectedBenchmark?.primaryDomains[0] ?? "";
        const competitorsCount = selectedBenchmark?.activationSet?.competitorsDomains?.length ?? 0;
        const competitorsKeys = Object.keys(competitorsData);

        const competitors: TableDataSchema[] = [];

        competitorsKeys.forEach((key: string) => {
            const currentCompetitor = competitorsData[key];
            const reachTrend = getTrend(currentCompetitor.reachTrend);

            if (competitorsCount > MAX_COMPETITORS_COUNT && competitors.length === MAX_COMPETITORS_COUNT_TO_SHOW) {
                return;
            }

            competitors.push({
                domain: key,
                audienceReach: currentCompetitor.reach,
                mom: currentCompetitor.periodOverPeriodReachDifference,
                trends: reachTrend,
                defaultIcon: domainImages[key],
                isPrimary: false,
                rank: currentCompetitor?.rank,
            });
        });

        competitors.push({
            domain: primaryDomain,
            audienceReach: primaryData.reach,
            mom: primaryData.periodOverPeriodReachDifference,
            trends: getTrend(primaryData.reachTrend),
            defaultIcon: domainImages[primaryDomain],
            isPrimary: true,
            rank: primaryData?.rank,
        });

        const sorterResult = sortBy(competitors, [(item) => item.rank]);

        if (competitorsCount > MAX_COMPETITORS_COUNT) {
            sorterResult.push({
                domain: translate("bt.report.audience.other.competitors", {
                    count: competitorsCount - MAX_COMPETITORS_COUNT_TO_SHOW,
                }),
                isPrimary: false,
                isOtherCompetitors: true,
            });
        }

        const change = primaryData.periodOverPeriodReachDifference;
        const convertedToPlusValues = change < 0 ? -change : change;

        setWidgetsData({
            tableData: sorterResult,
            chartData: {
                // @ts-expect-error FIXME: Type 'string | number' is not assignable to type 'string'.
                reach: simplePercentageFilter()(primaryData.reach, DEFAULT_DECIMAL),
                primaryDomain,
            },
            header: {
                value: FORMATTERS[format](primaryData.reach),
                change: `${FORMATTERS[MetricFormat.percent](convertedToPlusValues)} ${PERCENTAGE_POINT}`,
                isIncreasing: change > 0,
                isNotChanged: change === 0,
            },
        });
    }, [data, format, isLoading, domainImages]);

    return widgetsData;
};

export default useAudienceData;
